import React from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  Rocket,
  Clapperboard,
  Youtube,
  Hash,
  CalendarDays,
  MessageSquareQuote,
  ExternalLink,
  FileText,
  Tag,
  Gamepad2,
} from "lucide-react";

const VISUALS = [
  {
    label: "TikTok / Shorts — Vote live",
    ratio: "9:16",
    url: "https://static.prod-images.emergentagent.com/jobs/0a59ba63-879d-4d54-af4d-51df956ccf74/images/10cbc0544244932cbc8784f16330ed2b780d93f5cb7815be14205f9e761fc85e.jpeg",
  },
  {
    label: "Thumbnail YouTube — Défi",
    ratio: "16:9",
    url: "https://static.prod-images.emergentagent.com/jobs/0a59ba63-879d-4d54-af4d-51df956ccf74/images/bba85227a7b79a2f25706a716c6adbcc8ff1d39a3bb6dbcc757f64497777c5a3.jpeg",
  },
  {
    label: "Hype 5 carrières",
    ratio: "9:16",
    url: "https://static.prod-images.emergentagent.com/jobs/0a59ba63-879d-4d54-af4d-51df956ccf74/images/5b94d802bf8ed50e9e168e938b70ddaae3d67569c599b81c85d2a0d054102a94.jpeg",
  },
  {
    label: "Destin collectif / foule",
    ratio: "9:16",
    url: "https://static.prod-images.emergentagent.com/jobs/0a59ba63-879d-4d54-af4d-51df956ccf74/images/ddef046e3e67560fadc2520b98191f3189b205341c53a9e06a38acb02c58a2dc.jpeg",
  },
];

const CLIPS = [
  {
    title: "CLIP 1 — « Le chat décide » (Mode Live)",
    star: true,
    beats: [
      "0-3s : texte plein écran « JE LAISSE LE CHAT DÉCIDER DE MA CARRIÈRE » + voix « ok le chat, on devient quoi ? »",
      "3-8s : montre le QR/lien, 2-3 votes qui se remplissent en direct (barres roses), réactions 🔥💀 qui flottent",
      "8-20s : un événement RARE 🌟 ou un échec drôle → réaction faciale (caméra face)",
      "20-27s : reveal de la carte (rang + score)",
      "CTA : « Lien en bio, viens voter au prochain live »",
    ],
  },
  {
    title: "CLIP 2 — « 500 personnes ont ruiné mon destin »",
    beats: [
      "0-3s : « 500 PERSONNES ONT CHOISI MON DESTIN… » (montre le compteur de spectateurs)",
      "3-15s : montage rapide de 4-5 votes serrés, le public choisit toujours l'option chaotique",
      "15-25s : carte finale « ANONYME » ou score catastrophique → rire",
      "CTA : « le lien de défi collectif est en bio, bats ce destin »",
    ],
  },
  {
    title: "CLIP 3 — « 60 secondes pour devenir footballeur »",
    beats: [
      "0-3s : « TU AS 60 SECONDES POUR DEVENIR FOOTBALLEUR »",
      "3-20s : gameplay solo speedrun, stats qui montent, choix risqué en slow-mo",
      "20-27s : carte LÉGENDE",
      "CTA : « tu fais mieux ? lien en bio »",
    ],
  },
  {
    title: "CLIP 4 — « POV: événement légendaire »",
    beats: [
      "0-3s : « QUAND UN ÉVÉNEMENT LÉGENDAIRE TOMBE 🌟 »",
      "3-12s : zoom sur le badge doré + le choix à gros enjeu, musique qui monte",
      "12-20s : le score explose",
      "CTA : « ça n'arrive qu'1 fois sur 10 »",
    ],
  },
  {
    title: "CLIP 5 — « Streamer vs Rappeur : même joueur »",
    beats: [
      "0-3s : « MÊME JOUEUR, 2 DESTINS »",
      "3-20s : split/montage : une partie Streamer vs une partie Rappeur",
      "20-27s : compare les 2 cartes côte à côte",
      "CTA : « quel domaine je test ensuite ? »",
    ],
  },
];

const YT_STEPS = [
  {
    h: "1. La chaîne (base)",
    items: [
      "Nom : Destin Express (ou ton pseudo créateur)",
      "Photo de profil : le logo/key art (visuel « hype 5 carrières »)",
      "Bannière : décline le thumbnail 16:9 (2560×1440, garde le centre lisible)",
      "À propos : « Le jeu où TON public décide de ton destin en 60s. Joue gratuit → destinexpress.com »",
      "Liens : destinexpress.com + TikTok/Insta",
    ],
  },
  {
    h: "2. Deux formats à publier",
    items: [
      "Shorts (vertical) : recycle tes clips TikTok tels quels — c'est le moteur de découverte.",
      "Vidéos/Lives longs : sessions Mode Live où ton chat vote (10-30 min).",
    ],
  },
  {
    h: "3. Setup d'un LIVE YouTube",
    items: [
      "Logiciel : OBS Studio (gratuit).",
      "Source navigateur : Browser Source pointant sur ton Mode OBS (bouton dans le jeu) → plein écran ultra-lisible.",
      "Webcam : petite incrustation coin bas.",
      "À l'écran : affiche le QR code / code de room pour que les spectateurs votent.",
      "Clé de stream : YouTube Studio → « Créer » → « Passer au direct » → copie la clé RTMP dans OBS.",
    ],
  },
  {
    h: "4. Miniatures & titres",
    items: [
      "Utilise le thumbnail 16:9 + un gros titre 3-5 mots (« MON CHAT DÉCIDE »).",
      "Visage expressif + flèche/emoji = plus de clics.",
    ],
  },
  {
    h: "5. Métadonnées",
    items: [
      "Titre : accrocheur, mots-clés au début.",
      "Description : 2 lignes de hook + lien destinexpress.com + #DestinExpress.",
      "Tags/Hashtags : #DestinExpress #jeu #live #streamer.",
      "Écran de fin / carte : renvoie vers destinexpress.com et une autre vidéo.",
    ],
  },
  {
    h: "6. Rythme conseillé",
    items: [
      "1 Short/jour (recyclage TikTok) + 1 Live/semaine.",
      "Après chaque Live : coupe 2-3 Shorts des meilleurs moments.",
    ],
  },
];

const HOOKS = [
  { lang: "FR", lines: ["« Laisse mon chat décider de ma carrière 😳 »", "« 500 personnes ont choisi MON destin 💀 »", "« 60s pour devenir footballeur »"] },
  { lang: "EN", lines: ['"I let my chat decide my career 😳"', '"500 people chose MY destiny 💀"', '"60s to become a footballer"'] },
  { lang: "ES", lines: ['"Dejo que el chat decida mi carrera 😳"', '"500 personas eligieron MI destino 💀"', '"60s para ser futbolista"'] },
];

const HASHTAGS = [
  { lang: "FR", tags: "#DestinExpress #jeu #streamer #footballeur #rappeur #60secondes #twitch #fyp #pourtoi" },
  { lang: "EN", tags: "#DestinExpress #browsergame #streamer #careergame #60seconds #fyp #foryou #twitch" },
  { lang: "ES", tags: "#DestinExpress #juego #streamer #carrera #60segundos #parati #fyp" },
];

const PLAN = [
  "J1 : Short « Le chat décide » + post TikTok. Story Insta avec sondage.",
  "J2 : 1er Live test (Mode Live) → coupe 2 Shorts.",
  "J3 : Short « 500 personnes ont ruiné mon destin ». Reddit r/WebGames + r/playmygame.",
  "J4 : Short « événement légendaire ». DM à 5 petits streamers (pitch fourni).",
  "J5 : Product Hunt / « Show HN ». Repartage des meilleurs défis collectifs.",
  "Puis : soumission CrazyGames (mode portail prêt).",
];

const PITCHES = [
  {
    lang: "FR",
    text: "Salut ! J'ai un jeu gratuit taillé pour le live : Destin Express. Tu choisis une carrière (streamer, foot, rap, gaming, YouTube) et ton chat vote en direct sur chaque choix (QR à afficher). En 60s → carte de destin partageable. Mode OBS plein écran + réactions emoji + événements légendaires. Test : destinexpress.com",
  },
  {
    lang: "EN",
    text: "Hey! Free game made for streaming: Destin Express. Pick a career, your chat votes live on every choice (QR overlay). 60s → shareable destiny card. Fullscreen OBS mode + emoji reactions + legendary events. Try: destinexpress.com",
  },
];

const SUBMISSION = {
  title: "Destin Express",
  tags: [
    "casual", "decision", "simulation", "career", "life", "quick",
    "one-button", "choices", "funny", "singleplayer", "multiplayer", "streamer",
  ],
  category: "Casual / Simulation",
  langs: "Français, English, Español (auto-détection)",
  controls: {
    FR: "Souris (clic) ou tap sur l'écran pour choisir une option. C'est tout ! Jouable à une main, sur mobile comme sur ordinateur.",
    EN: "Mouse (click) or tap to pick an option. That's it! One-handed, works on mobile and desktop.",
    ES: "Ratón (clic) o toque para elegir una opción. ¡Eso es todo! Se juega con una mano, en móvil y ordenador.",
  },
  short: {
    FR: "En 60 secondes, choisis ta carrière et enchaîne des choix éclair pour devenir une légende. Ton destin, ta carte à partager !",
    EN: "In 60 seconds, pick your career and make rapid-fire choices to become a legend. Your destiny, your shareable card!",
    ES: "En 60 segundos, elige tu carrera y toma decisiones rápidas para volverte una leyenda. ¡Tu destino, tu carta para compartir!",
  },
  long: {
    FR: "Destin Express est un jeu de simulation de carrière éclair. Choisis un domaine parmi 5 (streamer, footballeur, rappeur, gamer, YouTubeur) puis prends des décisions binaires ultra-rapides qui font monter (ou chuter) tes stats : argent, gloire, énergie et chance. Attention au chrono de 60 secondes ! Tombe sur des événements rares et légendaires 🌟 qui peuvent tout changer, et découvre à la fin ton rang — de « Anonyme » à « Légende » — sur une carte de destin unique et partageable. Chaque partie est différente. Mode multijoueur en direct : ton public/chat peut voter sur tes choix via un QR code (parfait pour les streamers, avec un mode overlay OBS). Gratuit, sans installation, jouable en une main sur mobile et ordinateur.",
    EN: "Destin Express is a lightning-fast career simulation game. Pick one of 5 paths (streamer, footballer, rapper, gamer, YouTuber) then make ultra-quick binary decisions that raise (or crash) your stats: money, fame, energy and luck. Watch the 60-second timer! Hit rare and legendary events 🌟 that can change everything, and at the end discover your rank — from \"Nobody\" to \"Legend\" — on a unique, shareable destiny card. Every run is different. Live multiplayer mode: your audience/chat can vote on your choices via a QR code (perfect for streamers, with an OBS overlay mode). Free, no install, one-handed on mobile and desktop.",
    ES: "Destin Express es un juego de simulación de carrera relámpago. Elige uno de 5 caminos (streamer, futbolista, rapero, gamer, YouTuber) y toma decisiones binarias ultrarrápidas que suben (o hunden) tus estadísticas: dinero, fama, energía y suerte. ¡Cuidado con el cronómetro de 60 segundos! Encuentra eventos raros y legendarios 🌟 que pueden cambiarlo todo, y al final descubre tu rango — de «Anónimo» a «Leyenda» — en una carta de destino única para compartir. Cada partida es distinta. Modo multijugador en directo: tu público/chat puede votar tus decisiones con un código QR (perfecto para streamers, con modo overlay OBS). Gratis, sin instalación, se juega con una mano en móvil y ordenador.",
  },
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="mt-12">
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#f093fb] to-[#f5576c] text-white">
        <Icon size={18} />
      </div>
      <h2 className="font-display text-xl font-black uppercase tracking-tight text-white sm:text-2xl">
        {title}
      </h2>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

export const KitScreen = ({ onBack }) => {
  return (
    <div className="app-bg noise-overlay relative min-h-[100dvh] w-full">
      <div className="relative z-10 mx-auto w-full max-w-5xl px-5 py-8 sm:px-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="kit-back-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-transform hover:scale-105"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-white/60">
            <Rocket size={16} className="text-[#f093fb]" /> Destin Express
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8"
          data-testid="kit-screen"
        >
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
            Kit de lancement 🚀
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
            Tout ce qu'il te faut pour lancer Destin Express : visuels prêts à l'emploi,
            storyboards de clips, guide YouTube/Live, hooks multilingues, plan de la
            semaine 1 et pitchs streamers. Clique sur une image pour l'ouvrir en grand et l'enregistrer.
          </p>
        </motion.div>

        {/* Visuels */}
        <Section icon={Download} title="Visuels prêts à l'emploi">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {VISUALS.map((v, i) => (
              <a
                key={i}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`kit-visual-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-transform hover:-translate-y-1"
              >
                <img
                  src={v.url}
                  alt={v.label}
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3">
                  <div className="text-xs font-bold text-white">{v.label}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] text-white/60">
                    <span className="rounded bg-white/10 px-1.5 py-0.5">{v.ratio}</span>
                    <ExternalLink size={10} /> ouvrir
                  </div>
                </div>
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-white/40">
            Ajoute le texte par-dessus dans CapCut/Canva (garde l'espace laissé libre en haut/bas).
          </p>
        </Section>

        {/* Clips */}
        <Section icon={Clapperboard} title="5 clips prêts à tourner">
          <p className="-mt-2 mb-4 text-xs text-white/50">
            Format 9:16, 15-30s, écran du jeu en capture (OBS/enregistrement écran). Texte à l'écran = grosses capitales.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {CLIPS.map((c, i) => (
              <div
                key={i}
                data-testid={`kit-clip-${i}`}
                className={`rounded-2xl border p-5 ${
                  c.star
                    ? "border-[#f093fb]/40 bg-gradient-to-br from-[#f093fb]/10 to-[#f5576c]/5"
                    : "border-white/8 bg-white/[0.03]"
                }`}
              >
                <h3 className="font-display text-sm font-bold text-white">
                  {c.title} {c.star && <span className="text-[#f5d76e]">⭐ le plus viral</span>}
                </h3>
                <ul className="mt-3 space-y-2">
                  {c.beats.map((b, j) => (
                    <li key={j} className="flex gap-2 text-sm leading-relaxed text-white/70">
                      <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-[#f093fb]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] p-4 text-sm text-white/70">
            <strong className="text-white">Règle d'or :</strong> 1 clip/jour minimum, teste
            différents hooks, il suffit d'UN qui perce. Trafic organique uniquement (jamais de
            bots/achats → bannit AdSense).
          </p>
        </Section>

        {/* YouTube */}
        <Section icon={Youtube} title="YouTube — ce qu'il faut préparer">
          <div className="grid gap-4 md:grid-cols-2">
            {YT_STEPS.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                <h3 className="font-display text-sm font-bold text-white">{s.h}</h3>
                <ul className="mt-3 space-y-2">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-2 text-sm leading-relaxed text-white/70">
                      <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-[#f5576c]" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Hooks */}
        <Section icon={Hash} title="Hooks courts — FR / EN / ES">
          <div className="grid gap-4 sm:grid-cols-3">
            {HOOKS.map((h, i) => (
              <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                <div className="inline-block rounded-full bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-3 py-1 text-xs font-bold text-white">
                  {h.lang}
                </div>
                <ul className="mt-3 space-y-2">
                  {h.lines.map((l, j) => (
                    <li key={j} className="text-sm leading-relaxed text-white/70">{l}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {HASHTAGS.map((h, i) => (
              <div key={i} className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
                <span className="mr-2 text-xs font-bold text-[#f093fb]">{h.lang}</span>
                <span className="text-sm text-white/60">{h.tags}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Plan */}
        <Section icon={CalendarDays} title="Plan de lancement (semaine 1)">
          <ol className="space-y-3">
            {PLAN.map((p, i) => (
              <li key={i} className="flex gap-3 rounded-xl border border-white/8 bg-white/[0.03] p-4">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#f093fb] to-[#f5576c] text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="text-sm leading-relaxed text-white/70">{p}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Pitch */}
        <Section icon={MessageSquareQuote} title="Pitch streamers (DM à copier-coller)">
          <div className="space-y-4">
            {PITCHES.map((p, i) => (
              <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                <div className="mb-2 inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/70">
                  {p.lang}
                </div>
                <p className="text-sm italic leading-relaxed text-white/70">« {p.text} »</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Soumission CrazyGames */}
        <Section icon={FileText} title="Fiche de soumission CrazyGames">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50">
                <Gamepad2 size={14} className="text-[#f093fb]" /> Infos fiche
              </div>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex gap-2">
                  <dt className="w-24 flex-shrink-0 text-white/40">Titre</dt>
                  <dd className="font-semibold text-white">{SUBMISSION.title}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 flex-shrink-0 text-white/40">Catégorie</dt>
                  <dd className="text-white/80">{SUBMISSION.category}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 flex-shrink-0 text-white/40">Langues</dt>
                  <dd className="text-white/80">{SUBMISSION.langs}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 flex-shrink-0 text-white/40">Orientation</dt>
                  <dd className="text-white/80">Portrait (mobile-first)</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50">
                <Tag size={14} className="text-[#f5576c]" /> Tags
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUBMISSION.tags.map((tg) => (
                  <span
                    key={tg}
                    className="rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-semibold text-white/70"
                  >
                    {tg}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Descriptions multilingues */}
          {["FR", "EN", "ES"].map((lg) => (
            <div key={lg} className="mt-4 rounded-2xl border border-white/8 bg-white/[0.03] p-5">
              <div className="mb-3 inline-block rounded-full bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-3 py-1 text-xs font-bold text-white">
                {lg}
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">Description courte</p>
              <p className="mt-1 text-sm leading-relaxed text-white/80">{SUBMISSION.short[lg]}</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-widest text-white/40">Description longue</p>
              <p className="mt-1 text-sm leading-relaxed text-white/70">{SUBMISSION.long[lg]}</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-widest text-white/40">Contrôles</p>
              <p className="mt-1 text-sm leading-relaxed text-white/70">{SUBMISSION.controls[lg]}</p>
            </div>
          ))}
        </Section>

        <div className="mt-14 border-t border-white/8 pt-6 text-center text-xs text-white/40">
          Destin Express — kit de lancement · destinexpress.com
        </div>
      </div>
    </div>
  );
};
